import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db7re0gmkl3spn8smc40";

const values = [
  { icon: "ri-stack-line", title: "资源聚合", desc: "汇集双女主题材韩漫，涵盖校园、奇幻、恋爱、悬疑等丰富分类。" },
  { icon: "ri-refresh-line", title: "持续更新", desc: "热门作品章节同步更新，第一时间追到最新章节。" },
  { icon: "ri-device-line", title: "全端适配", desc: "电脑与手机都能清晰阅读，随时随地畅快浏览。" },
  { icon: "ri-money-cny-circle-line", title: "完全免费", desc: "基础阅读功能免费开放，无需付费即可浏览全部演示内容。" },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "关于我们 - 韩漫免费漫画在线观看阅读双女主",
    description:
      "双女漫是专注双女主剧情的韩漫在线阅读平台，提供免费浏览、分类筛选与最新章节追更，欢迎通过反馈表单与我们联系。",
    keywords: "双女漫,关于我们,韩漫平台,双女主漫画,漫画推荐",
    canonicalPath: "/about",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setFormError(serverMsg || "提交失败，请稍后重试，或通过邮箱联系我们。");
        setStatus("error");
        return;
      }

      setStatus("success");
      formEl.reset();
    } catch {
      setFormError("网络异常，提交失败，请稍后重试，或通过邮箱联系我们。");
      setStatus("error");
    }
  };

  return (
    <div className="w-full">
      <section className="bg-primary-500">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
          <nav className="mb-5 flex items-center gap-2 text-sm text-primary-100">
            <Link to="/" className="transition-colors hover:text-white">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white">关于我们</span>
          </nav>
          <h1 className="font-heading text-3xl font-bold text-white md:text-4xl">关于双女漫</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-primary-50 md:text-base">
            双女漫是一个专注双女主剧情的韩漫免费在线阅读平台。我们相信，每一个动人的故事都值得被看见，
            每一份温柔的情感都应该被认真对待。这里汇聚了校园、奇幻、恋爱、悬疑等多种题材的双女主韩漫，
            希望能陪伴你度过每一个追更的夜晚。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
        <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">平台特色</h2>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${item.icon} text-2xl`} />
              </span>
              <h3 className="mt-4 text-sm font-semibold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系方式</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              如果你对内容、版权或使用体验有任何建议，欢迎随时与我们联系，我们会尽快回复。
            </p>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className="ri-mail-line text-lg" />
                </span>
                <span>
                  <span className="block font-medium text-foreground-950">客服邮箱</span>
                  <span className="block text-foreground-600">support@shuangnv-comic.example</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-customer-service-2-line text-lg" />
                </span>
                <span>
                  <span className="block font-medium text-foreground-950">在线时间</span>
                  <span className="block text-foreground-600">周一至周五 10:00 - 18:00</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-map-pin-line text-lg" />
                </span>
                <span>
                  <span className="block font-medium text-foreground-950">办公地址</span>
                  <span className="block text-foreground-600">首尔特别市 麻浦区 读书街 12</span>
                </span>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
            <h2 className="font-heading text-xl font-bold text-foreground-950">意见反馈</h2>
            <p className="mt-2 text-sm text-foreground-600">填写下面的表单，把想法告诉我们。</p>

            {status === "success" ? (
              <div className="mt-6 rounded-lg border border-secondary-200 bg-secondary-50 p-8 text-center">
                <i className="ri-checkbox-circle-line text-4xl text-secondary-600" />
                <p className="mt-3 text-sm font-medium text-secondary-900">感谢你的反馈，我们已收到！</p>
                <p className="mt-1 text-xs text-secondary-700">我们会认真阅读每一条建议，并尽快处理。</p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-5 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-background-50 px-5 py-2 text-sm font-medium text-secondary-800 transition-colors hover:bg-secondary-100"
                >
                  再提交一条
                </button>
              </div>
            ) : (
              <form id="about-feedback-form" data-readdy-form onSubmit={handleSubmit} className="mt-5 space-y-4">
                <div>
                  <label htmlFor="fb-name" className="mb-1.5 block text-xs font-medium text-foreground-800">昵称</label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="fb-email" className="mb-1.5 block text-xs font-medium text-foreground-800">邮箱</label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复，例如 name@example.com"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="fb-topic" className="mb-1.5 block text-xs font-medium text-foreground-800">反馈类型</label>
                  <select
                    id="fb-topic"
                    name="topic"
                    defaultValue="内容建议"
                    className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="功能反馈">功能反馈</option>
                    <option value="版权相关">版权相关</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="fb-message" className="mb-1.5 block text-xs font-medium text-foreground-800">详细内容</label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请描述你的建议或问题（500 字以内）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="field-website-alt"
                />

                {status === "error" && formError && (
                  <p className="rounded-md border border-accent-300 bg-accent-50 px-3 py-2 text-xs text-accent-700">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg" /> 提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" /> 提交反馈
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-copyright-line text-primary-600" /> 版权信息
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站所有漫画封面、文字与章节内容均为演示样例，仅用于展示平台的功能与浏览体验，不代表真实作品。
              如涉及版权问题，请通过上方联系方式与我们取得联系，我们会及时处理。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-error-warning-line text-accent-600" /> 免责声明
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本平台为演示性质站点，不提供真实的漫画下载与付费服务，章节阅读功能为静态演示内容，待正式版权合作与后端接口接入。
              用户因使用本站产生的任何问题，本站不承担相应责任。
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}